import * as React from 'react';
import { useRef, useState } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { Button, Checkbox, DatePicker, Input, Modal, Popconfirm, Popover, Select } from 'antd';
import {
	ClockCircleOutlined,
	DeleteOutlined,
	FileDoneOutlined,
	PlusOutlined,
	UserOutlined,
} from '@ant-design/icons';
import { withUserType } from '../withUserType';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';
import { EmptyState } from '../empty_state/EmptyState';
import useMountEffect from '../../hooks/useMountEffect';
import { AdvancedFilters } from '../advanced_filters/AdvancedFilters';
import SelectedFilters from '../selected_filters_pills/SelectedFilters';
import { Sorter } from '../WorkOrderTable/sorters/Sorters';
import InlineEdit from '../ow_inline_todo_list/InlineEdit';
import DateTimeHover from '../date_time_component/DateTime';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { runWithMessageFeedback } from '../../utils/MessageFeedbackUtils';
import { PROJECT_TASKS_CRUD_ACTION_CREATORS } from '../../actions/project_tasks_actions';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { cn } from '../../utils/ClassNamesUtils';
import { FILTER_FIELD_TYPE, ROLE_TYPES } from '../../utils/DataConstants';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { debounce } from '../../utils/PerformanceUtils';
import { getContactName } from '../../utils/DataFormatterUtils';
import {
	projectTasksRestCrudThunksForBuyer,
	projectTasksRestCrudThunksForSupplier,
} from '../../thunks/project_tasks_thunks';
import {
	deliverablesRestCrudThunksForBuyer,
	deliverablesRestCrudThunksForSupplier,
} from '../../thunks/deliverables_thunks';
import {
	buyerContactsRestCrudThunksForBuyer,
	buyerContactsRestCrudThunksForSupplier,
} from '../../thunks/buyer_contacts_thunks';
import {
	projectsRestCrudThunksForBuyer,
	projectsRestCrudThunksForSupplier,
} from '../../thunks/projects_thunks';

require('./DeliverableTasks.less');

// A ProjectTask always belongs to a project (projectId) and MAY belong to a
// deliverable (deliverableId). Drives both the deliverable Tasks sub-tab
// (projectId + deliverableId) and the project Tasks tab (projectId only).
// Work-order association is intentionally NOT built — ProjectTask has no
// workOrderId field yet.
//
// The row layout deliberately mirrors the work order task list
// (ow_inline_todo_list/EachWOTask + OWTaskComponent): checkbox and inline-editable
// title on one line, actions revealed on hover, set values on a muted line
// underneath, and a "+ Add task" link that opens a blank row. Those components are
// shared by WO detail, PM templates and walkthroughs and are bound to the task_item
// shape, so this matches their UX rather than reusing them.
//
// Own contacts collection — the deliverable form's assignee picker uses its own
// (DELIVERABLE_FORM_CONTACTS_AUTOCOMPLETE) and both mount together inside the edit
// modal, so sharing one would let a task-assignee search overwrite the modal's list.
const CONTACTS_TC = 'PROJECT_TASK_CONTACTS_AUTOCOMPLETE';
// The Assignee FILTER gets its own collection. Sharing the row picker's would let a
// row-level assignee search replace the filter's options, and SelectedFilters builds
// the active pill by matching the stored email against those options — so the pill
// would vanish while the filter stayed applied.
const FILTER_CONTACTS_TC = 'PROJECT_TASK_FILTER_CONTACTS_AUTOCOMPLETE';
const DELIVERABLES_TC = 'PROJECT_TASKS_DELIVERABLES';
const CONTACTS_PAGE_SIZE = 25;

const tcFor = (deliverableId) => (deliverableId ? 'DELIVERABLE_TASKS' : 'PROJECT_TASKS');

const contactName = (contact) =>
	getContactName(contact).trim() || nullSafeGetOrElse('email', contact, '');

// Full re-send on update; projectId/deliverableId numeric, dueDate ISO;
// completedAt is backend-managed. deliverableId is preserved unless overridden.
const toTaskPayload = (task, projectId, overrides: any = {}) => {
	const dueDate = nullSafeGet('dueDate', task);
	const deliverableId = nullSafeGet('deliverableId', task);
	return {
		id: nullSafeGet('id', task),
		projectId: Number(projectId),
		deliverableId: deliverableId != null ? Number(deliverableId) : undefined,
		title: nullSafeGet('title', task),
		assignedTo: nullSafeGet('assignedTo', task),
		dueDate: dueDate ? moment(dueDate).toISOString() : undefined,
		isCompleted: nullSafeGetOrElse('isCompleted', task, false),
		orderingId: nullSafeGet('orderingId', task),
		...overrides,
	};
};

// Sort options (shared Sorter item shape: label / selectVal / value:{sort_by,
// order}); `order` is antd-style and the read thunk maps 'ascend'→'asc'. Due date
// first and as the default — created-order alone reads as random, which was the
// Pet Valu complaint. The backend sorts dueDate with NULLs last in both directions.
const TASK_SORTERS = [
	{ label: 'Earliest due', selectVal: 'dueDate', value: { sort_by: 'dueDate', order: 'ascend' } },
	{ label: 'Newest', selectVal: 'createdAt', value: { sort_by: 'createdAt', order: 'desc' } },
	{ label: 'Oldest', selectVal: 'createdAtAsc', value: { sort_by: 'createdAt', order: 'ascend' } },
];
const DEFAULT_SORTING = TASK_SORTERS[0].value;

const STATUS_OPTIONS = [
	{ value: 'all', label: 'All' },
	{ value: 'incomplete', label: 'Incomplete' },
	{ value: 'complete', label: 'Complete' },
];

// Static-options select used as an AdvancedFilters filterConfig `component`.
const StaticOptionsSelect = ({ value, onChange, items, placeholder, style }: any) => (
	<Select
		allowClear
		value={value}
		onChange={onChange}
		placeholder={placeholder}
		style={style || { width: '100%' }}
		options={items}
	/>
);

// Assignee filter control: the same contact list the row's assignee popover uses,
// searched on the server (filterOption off) so the choices aren't capped at the first
// page. AdvancedFilters forwards a fixed set of props to a filterConfig `component`,
// which does not include a search handler, so the caller wraps this with its own.
const ContactOptionsSelect = ({ value, onChange, items, onSearch, placeholder, style }: any) => (
	<Select
		allowClear
		showSearch
		filterOption={false}
		value={value}
		onChange={onChange}
		onSearch={onSearch}
		placeholder={placeholder}
		style={style || { width: '100%' }}
		options={items}
	/>
);

// Only-date quick presets (no time), matching the Figma "In 7d / In 1mo / In 3mo".
const QUICK_DATES = [
	{ label: 'In 7d', amount: 7, unit: 'days' },
	{ label: 'In 1mo', amount: 1, unit: 'months' },
	{ label: 'In 3mo', amount: 3, unit: 'months' },
];

// Due-date popover: quick presets and a date-only calendar. The trigger is the
// caller's icon, so the same popover serves the row action and the new-task row.
const DuePopover = ({ value, onChange, children }: any) => {
	const [open, setOpen] = useState(false);
	const pick = (next) => {
		onChange(next || null);
		setOpen(false);
	};
	const content = (
		<div style={{ width: 280 }}>
			<div className="mb-2 flex gap-2">
				{QUICK_DATES.map((quick) => (
					<Button
						key={quick.label}
						size="small"
						onClick={() =>
							pick(
								moment()
									.add(quick.amount, quick.unit as any)
									.startOf('day')
							)
						}
					>
						{quick.label}
					</Button>
				))}
			</div>
			<DatePicker
				value={value}
				format="MMM D, YYYY"
				style={{ width: '100%' }}
				getPopupContainer={(trigger) => trigger.parentNode}
				onChange={(date) => pick(date ? date.startOf('day') : null)}
			/>
			{value ? (
				<Button type="link" size="small" className="mt-1 px-0" onClick={() => pick(null)}>
					Clear due date
				</Button>
			) : null}
		</div>
	);
	return (
		<Popover
			trigger="click"
			visible={open}
			onVisibleChange={setOpen}
			content={content}
			placement="bottomRight"
		>
			{children}
		</Popover>
	);
};

// Assignee popover: search + contact list (avatar / name / email).
const AssignPopover = ({ email, contacts, onSearch, onChange, children }: any) => {
	const [open, setOpen] = useState(false);
	const pick = (next) => {
		onChange(next);
		setOpen(false);
	};
	const content = (
		<div style={{ width: 288 }}>
			<div className="footnoteregular mb-2 text-character-secondary">ASSIGN TO</div>
			<Input
				allowClear
				placeholder="Search people"
				className="mb-2"
				onChange={(event) => onSearch(event.target.value)}
			/>
			<div className="max-h-64 overflow-y-auto">
				<div
					className="flex cursor-pointer items-center gap-2 rounded px-1 py-1.5 hover:bg-grey-2"
					onClick={() => pick(null)}
				>
					<span className="flex size-8 items-center justify-center rounded-full bg-grey-6 text-white">
						<UserOutlined />
					</span>
					<span className="bodyregular text-character-primary">No Assignee</span>
				</div>
				{(contacts || []).map((contact) => {
					const contactEmail = nullSafeGetOrElse('email', contact, '');
					return (
						<div
							key={contactEmail}
							onClick={() => pick(contactEmail)}
							className={cn(
								'flex cursor-pointer items-center gap-2 rounded px-1 py-1.5 hover:bg-grey-2',
								contactEmail === email && 'bg-purple-1'
							)}
						>
							<ContactAvatar contact={contact} size="small" hidePopover />
							<div className="flex flex-col">
								<span className="bodyregular text-character-primary">{contactName(contact)}</span>
								<span className="footnoteregular text-character-secondary">{contactEmail}</span>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
	return (
		<Popover
			trigger="click"
			visible={open}
			onVisibleChange={setOpen}
			content={content}
			placement="bottomRight"
		>
			{children}
		</Popover>
	);
};

// The muted line under a task title — the work order row's "additional details"
// slot. Display only: everything on it is edited through the hover actions.
const TaskDetailLine = ({ task, deliverableName, isProjectScope }: any) => {
	const dueDate = nullSafeGet('dueDate', task);
	const assignedTo = nullSafeGet('assignedTo', task);
	const isCompleted = nullSafeGetOrElse('isCompleted', task, false);
	// completedAt / completedBy are backend-stamped. UserNameWithEmail resolves the
	// email to a name (fetching the contact if needed), so no local list is required.
	const completedAt = nullSafeGet('completedAt', task);
	const completedByEmail = nullSafeGet('completedBy', task);
	const showCompleted = isCompleted && (completedByEmail || completedAt);
	const deliverableId = nullSafeGet('deliverableId', task);

	if (!dueDate && !assignedTo && !showCompleted && !(isProjectScope && deliverableId)) {
		return null;
	}

	return (
		<div className="footnoteregular ml-2 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-character-secondary">
			{dueDate ? <span>Due {moment(dueDate).format('MMM D')}</span> : null}
			{assignedTo ? (
				<span className="flex items-center gap-1">
					<span>Assigned to</span>
					<UserNameWithEmail email={assignedTo} mode="inline" placement="top" />
				</span>
			) : null}
			{isProjectScope && deliverableId ? (
				<span className="flex items-center gap-1">
					<FileDoneOutlined className="text-[12px]" />
					{deliverableName}
				</span>
			) : null}
			{showCompleted ? (
				<span className="flex items-center gap-1">
					<span>Completed</span>
					{completedByEmail ? (
						<>
							<span>by</span>
							<UserNameWithEmail email={completedByEmail} mode="inline" placement="top" />
						</>
					) : null}
					{completedAt ? (
						<>
							<span>·</span>
							{/* DateTimeHover renders in the viewer's timezone and appends the
							    tz abbreviation (z) — no manual moment formatting. */}
							<DateTimeHover timestamp={completedAt} showDate showTime />
						</>
					) : null}
				</span>
			) : null}
		</div>
	);
};

const TaskRow = ({
	task,
	isProjectScope,
	deliverableName,
	contacts,
	onSearchContacts,
	onToggle,
	onSaveTitle,
	onChangeDue,
	onChangeAssignee,
	onRequestLinkDeliverable,
	onDelete,
}: any) => {
	const title = nullSafeGetOrElse('title', task, '');
	const isCompleted = nullSafeGetOrElse('isCompleted', task, false);
	const [editing, setEditing] = useState(false);

	// InlineEdit calls setValue on blur/Enter (its exit hook). For an actual
	// change, stay in edit mode until the save resolves — by then the store has
	// the new title, so dropping to the display text doesn't flash the old value
	// then the new one. No-op / empty edits exit immediately.
	const commitTitle = (value) => {
		const next = (value || '').trim();
		if (!next || next === title) {
			setEditing(false);
			return;
		}
		const result = onSaveTitle(task, next);
		if (result && result.finally) {
			result.finally(() => setEditing(false));
		} else {
			setEditing(false);
		}
	};

	return (
		<div className="group mb-2 flex flex-row items-start">
			<div className="pt-1">
				<Checkbox checked={!!isCompleted} onChange={() => onToggle(task)} />
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex flex-row items-start">
					{editing ? (
						<InlineEdit
							value={title}
							onEnter={() => {}}
							setValue={commitTitle}
							className={cn('bodyregular', isCompleted && 'text-character-disabled line-through')}
						/>
					) : (
						<div
							onClick={() => setEditing(true)}
							className={cn(
								'bodyregular pl-2 pt-1 hover:cursor-pointer',
								isCompleted ? 'text-character-disabled line-through' : 'text-character-primary'
							)}
						>
							{title}
						</div>
					)}
					{/* `hidden` beats `flex` in Tailwind's stylesheet order and the hover
					    variant beats both, so the cluster only appears on row hover —
					    same idiom as the work order task row.
					    h-[22px] + mt-1 gives the icons the same line box as the title's first
					    line (bodyregular is 14/22, and the title's own pt-1 is the 4px), so
					    centring inside it lands them on the text's optical centre. Without the
					    explicit height the 14px glyphs centre 4px too high. */}
					<div className="ml-4 mt-1 flex hidden h-[22px] flex-row items-center gap-4 text-character-secondary hover:cursor-pointer group-hover:flex">
						<DuePopover
							value={nullSafeGet('dueDate', task) ? moment(nullSafeGet('dueDate', task)) : null}
							onChange={(date) => onChangeDue(task, date)}
						>
							<ClockCircleOutlined title="Due date" />
						</DuePopover>
						<AssignPopover
							email={nullSafeGet('assignedTo', task)}
							contacts={contacts}
							onSearch={onSearchContacts}
							onChange={(email) => onChangeAssignee(task, email)}
						>
							<UserOutlined title="Assignee" />
						</AssignPopover>
						{isProjectScope ? (
							<PlusOutlined
								title="Link deliverable"
								onClick={() => onRequestLinkDeliverable(task)}
							/>
						) : null}
						<Popconfirm
							placement="topRight"
							title="Delete this task?"
							okText="Delete"
							okButtonProps={{ danger: true }}
							cancelText="Cancel"
							onConfirm={() => onDelete(task)}
						>
							<DeleteOutlined title="Delete" />
						</Popconfirm>
					</div>
				</div>
				<TaskDetailLine
					task={task}
					deliverableName={deliverableName}
					isProjectScope={isProjectScope}
				/>
			</div>
		</div>
	);
};

// A blank row in edit mode, like the work order list's "+ Add task": title only,
// saved on Enter/blur. Due date and assignee are set from the row's own actions
// once it exists.
const NewTaskRow = ({ onCreate, onCancel }: any) => {
	const commitTitle = (value) => {
		const next = (value || '').trim();
		if (!next) {
			onCancel();
			return;
		}
		onCreate(next);
	};

	return (
		<div className="mb-2 flex flex-row items-start">
			<div className="pt-1">
				<Checkbox disabled />
			</div>
			<div className="min-w-0 flex-1">
				<InlineEdit
					value=""
					placeholder="Enter task title"
					onEnter={() => {}}
					setValue={commitTitle}
					className="bodyregular"
				/>
			</div>
		</div>
	);
};

const DeliverableTasks = ({
	projectId,
	deliverableId,
	// Server-computed { tasksCompleted, tasksTotal } for this scope — taskMetrics on
	// a deliverable, taskProjectMetrics on a project. The header fraction has to come
	// from the server: the table holds one page, so a page-local count would undercount.
	metrics,
	contacts,
	filterContacts,
	deliverables,
	tasksSlice,
	createTask,
	updateTask,
	deleteTask,
	fetchTasksForList,
	fetchContacts,
	fetchFilterContacts,
	fetchDeliverables,
	refreshDeliverable,
	refreshProject,
	updateFilters,
	updateSorting,
	clearAndUpdateFilters,
}: any) => {
	const isProjectScope = deliverableId == null;
	const tc = tcFor(deliverableId);

	const [adding, setAdding] = useState(false);
	const [linkTask, setLinkTask] = useState(null);
	const [linkDeliverableId, setLinkDeliverableId] = useState(undefined);
	const [refreshNonce, setRefreshNonce] = useState(0);

	const debouncedContactSearch = useRef(debounce((term) => fetchContacts(term), 400, false));
	const onSearchContacts = (term) => debouncedContactSearch.current(term);

	// Separate debounce for the filter's own collection, so the two searches never
	// overwrite each other's options.
	const debouncedFilterContactSearch = useRef(
		debounce((term) => fetchFilterContacts(term), 400, false)
	);
	const onSearchFilterContacts = (term) => debouncedFilterContactSearch.current(term);

	// Stable identity: AdvancedFilters renders filterConfig.component directly, so a
	// wrapper rebuilt each render would remount the select and drop its open state.
	const assigneeFilterComponent = useRef((props) => (
		<ContactOptionsSelect {...props} onSearch={onSearchFilterContacts} />
	));

	// Base scope + a refresh nonce. PaginatedReduxTable passes this straight to the
	// fetch thunk (which strips the nonce); the deliverable/project scope is constant
	// per mount, the nonce changes only to force a post-mutation refetch.
	const baseScope = isProjectScope
		? { projectId: Number(projectId) }
		: { deliverableId: Number(deliverableId) };
	const additionalParams = { ...baseScope, _refresh: refreshNonce };

	useMountEffect(() => {
		// Reset any task filters persisted in the shared slice from a previous
		// project/deliverable, so opening this Tasks view doesn't carry a stale
		// deliverableId (which would send it with the new projectId and show nothing).
		clearAndUpdateFilters({}, tc);
		fetchContacts('');
		fetchFilterContacts('');
		if (isProjectScope && projectId) {
			fetchDeliverables(projectId);
		}
	});

	// AdvancedFilters writes status / deliverableId into the project_tasks slice;
	// PaginatedReduxTable reads those slice filters and refetches on change, so the
	// wrappers only need to update the slice (translation to server params happens
	// in the fetch thunk).
	const applyUpdateFilters = (patch, targetCollectionName) => {
		updateFilters(patch, targetCollectionName);
	};
	const applyClearAndUpdateFilters = (patch, targetCollectionName) => {
		clearAndUpdateFilters(patch, targetCollectionName);
	};

	// The header fraction comes from the parent record's server metrics, so both it
	// and the list have to be refetched after every write.
	const refresh = () => {
		setRefreshNonce((nonce) => nonce + 1);
		if (deliverableId != null) {
			refreshDeliverable(deliverableId);
		} else if (projectId) {
			refreshProject(projectId);
		}
	};

	const deliverableNameById = (id) => {
		const match = (deliverables || []).find((entry) => `${nullSafeGet('id', entry)}` === `${id}`);
		return match ? nullSafeGet('name', match) : `Deliverable #${id}`;
	};

	// Task writes aren't optimistic (the list only updates after the refetch), so wrap
	// each op in an antd message that transitions loading -> success/error.
	const runWithFeedback = (promise, loading, success, failure) =>
		runWithMessageFeedback(promise, { key: 'deliverable-task-op', loading, success, failure });

	const handleCreate = (title) => {
		const entity: any = {
			projectId: Number(projectId),
			isCompleted: false,
			title,
		};
		if (deliverableId != null) {
			entity.deliverableId = Number(deliverableId);
		}
		// Close the row only once the create succeeds — runWithMessageFeedback reports the
		// error and resolves undefined, so a failure keeps the row and the typed title
		// instead of making the user retype it.
		runWithFeedback(createTask(entity), 'Adding task…', 'Task added', 'Failed to add task').then(
			(record) => {
				if (record) {
					setAdding(false);
					refresh();
				}
			}
		);
	};

	const patchTask = (task, overrides, { loading, success, failure }) =>
		runWithFeedback(
			updateTask(toTaskPayload(task, projectId, overrides)),
			loading,
			success,
			failure
		).then(refresh);

	const handleToggle = (task) =>
		patchTask(
			task,
			{ isCompleted: !task.isCompleted },
			{
				loading: 'Updating task…',
				success: task.isCompleted ? 'Task marked incomplete' : 'Task completed',
				failure: 'Failed to update task',
			}
		);
	const handleSaveTitle = (task, title) =>
		patchTask(
			task,
			{ title },
			{
				loading: 'Saving…',
				success: 'Task updated',
				failure: 'Failed to update task',
			}
		);
	const handleChangeDue = (task, date) =>
		patchTask(
			task,
			{ dueDate: date ? date.startOf('day').toISOString() : null },
			{
				loading: 'Updating due date…',
				success: 'Due date updated',
				failure: 'Failed to update due date',
			}
		);
	const handleChangeAssignee = (task, email) =>
		patchTask(
			task,
			{ assignedTo: email == null ? null : email },
			{
				loading: 'Updating assignee…',
				success: 'Assignee updated',
				failure: 'Failed to update assignee',
			}
		);
	const handleDelete = (task) =>
		runWithFeedback(
			deleteTask(task),
			'Deleting task…',
			'Task deleted',
			'Failed to delete task'
		).then(refresh);

	const applyLinkDeliverable = () => {
		patchTask(
			linkTask,
			{ deliverableId: linkDeliverableId != null ? Number(linkDeliverableId) : null },
			{
				loading: 'Linking…',
				success: 'Deliverable linked',
				failure: 'Failed to link deliverable',
			}
		);
		setLinkTask(null);
		setLinkDeliverableId(undefined);
	};

	// The slice's filter values for this scope — SelectedFilters reads them by field
	// name to decide which pills to show.
	const activeFilters = nullSafeGetOrElse(`${tc}.filters`, tasksSlice, {});

	// PaginatedReduxTable takes its sorting from the slice and refetches when it
	// changes, so the Sorter writes there rather than holding its own copy.
	// The slice starts with an empty sorting object, so fall back until the first
	// fetch stores one — otherwise the Sorter would show its placeholder on mount.
	const sliceSorting = nullSafeGet(`${tc}.sorting`, tasksSlice);
	const activeSorting = nullSafeGet('sort_by', sliceSorting) ? sliceSorting : DEFAULT_SORTING;
	const onSortChange = (nextSorting) => updateSorting(nextSorting.sort_by, nextSorting.order, tc);

	// "(completed/total)" from the server metrics, like the work order Task List
	// header; the slice count is the fallback when a caller has no metrics to pass.
	const totalCount = nullSafeGetOrElse(`${tc}.count`, tasksSlice, 0);
	const headerCount = metrics
		? `${nullSafeGetOrElse('tasksCompleted', metrics, 0)}/${nullSafeGetOrElse(
				'tasksTotal',
				metrics,
				0
		  )}`
		: `${totalCount}`;

	// Only backend-supported filters: status (isCompleted) and, in project scope,
	// deliverable (deliverableId).
	const filterConfig = [
		{
			// RADIO_GROUP: a single-select over static `items`, which is what SelectedFilters
			// needs to build a pill (it reads the label off the matching item) and to clear
			// the field when the pill's x is clicked.
			label: 'Status',
			fieldName: 'status',
			type: FILTER_FIELD_TYPE.RADIO_GROUP,
			placeholder: 'Status',
			component: StaticOptionsSelect,
			items: STATUS_OPTIONS,
			renderItem: (option) => option.label,
			valueAccessor: (option) => option.value,
			filtersValueAccessor: (slice) => nullSafeGet(`${tc}.filters.status`, slice),
			// 'all' means "don't narrow", so store it as no filter at all — otherwise it
			// shows as a "Status is All" pill that removes nothing.
			handleChange: (value) =>
				applyUpdateFilters({ status: value === 'all' ? undefined : value }, tc),
		},
		{
			label: 'Assignee',
			fieldName: 'assignedTo',
			type: FILTER_FIELD_TYPE.RADIO_GROUP,
			placeholder: 'Assignee',
			component: assigneeFilterComponent.current,
			items: (filterContacts || []).map((contact) => ({
				value: nullSafeGetOrElse('email', contact, ''),
				label: contactName(contact),
			})),
			renderItem: (option) => option.label,
			valueAccessor: (option) => option.value,
			filtersValueAccessor: (slice) => nullSafeGet(`${tc}.filters.assignedTo`, slice),
			handleChange: (value) => applyUpdateFilters({ assignedTo: value }, tc),
		},
		...(isProjectScope
			? [
					{
						label: 'Deliverable',
						fieldName: 'deliverableId',
						type: FILTER_FIELD_TYPE.RADIO_GROUP,
						placeholder: 'Deliverable',
						component: StaticOptionsSelect,
						items: (deliverables || []).map((entry) => ({
							value: nullSafeGet('id', entry),
							label: nullSafeGet('name', entry),
						})),
						renderItem: (option) => option.label,
						valueAccessor: (option) => option.value,
						filtersValueAccessor: (slice) => nullSafeGet(`${tc}.filters.deliverableId`, slice),
						handleChange: (value) => applyUpdateFilters({ deliverableId: value }, tc),
					},
			  ]
			: []),
	];

	// Either the blank row being typed into or the link that opens one. Lives in the
	// table's footer, so it sits directly under the last row.
	const addTaskRow = adding ? (
		<NewTaskRow onCreate={handleCreate} onCancel={() => setAdding(false)} />
	) : (
		<div className="padding-less-antd-button flex flex-row pl-1">
			<Button type="link" onClick={() => setAdding(true)}>
				<PlusOutlined /> Add task
			</Button>
		</div>
	);

	return (
		<div>
			{/* Header row and the active-filter pills share one block, so the divider sits
			    between the whole control area and the list — not between the controls and
			    the pills they produced. */}
			<div className="mb-3 border-0 border-b border-solid border-grey-4 pb-3">
				<div className="flex items-center justify-between gap-3">
					<span className="h5medium shrink-0 text-character-primary">
						Task List ({headerCount})
					</span>
					<div className="flex flex-1 items-center gap-3">
						<Sorter items={TASK_SORTERS} value={activeSorting} onChange={onSortChange} />
						<AdvancedFilters
							filtersTargetCollectionName={tc}
							filtersStateSlice={tasksSlice}
							clearAndUpdateFilters={applyClearAndUpdateFilters}
							updateFilters={applyUpdateFilters}
							filterConfig={filterConfig}
							showSearch={false}
						/>
					</div>
				</div>
				{/* Pills for whatever is active, with an x to clear each one. SelectedFilters
			    renders nothing when no filter is set, and containerClassName carries the
			    spacing, so no empty wrapper is left behind to eat a margin. */}
				<SelectedFilters
					updateFilters={(patch) => applyUpdateFilters(patch, tc)}
					filterValues={activeFilters}
					filterConfig={filterConfig}
					pillsClosable
					containerClassName="mt-3 flex flex-wrap items-center gap-2"
				/>
			</div>

			<div className="deliverable-tasks-table">
				<PaginatedReduxTable
					collectionName="project_tasks"
					targetCollectionName={tc}
					additionalParams={additionalParams}
					initialPagination={{ current: 1, pageSize: 20 }}
					initialFilters={{}}
					initialSorting={DEFAULT_SORTING}
					showHeader={false}
					keyAccessor={(task) => task.id}
					rowClassName="deliverable-task-row"
					emptyState={
						<EmptyState headline="No tasks yet" body={<p>Add a task to track work here.</p>} />
					}
					fetchData={fetchTasksForList}
					// antd renders the footer inside the table wrapper, above the pager, so the
					// add affordance stays with the rows instead of below the pagination.
					footer={() => addTaskRow}
					columns={[
						{
							key: 'task',
							render: (_text, task) => (
								<TaskRow
									task={task}
									isProjectScope={isProjectScope}
									deliverableName={deliverableNameById(nullSafeGet('deliverableId', task))}
									contacts={contacts}
									onSearchContacts={onSearchContacts}
									onToggle={handleToggle}
									onSaveTitle={handleSaveTitle}
									onChangeDue={handleChangeDue}
									onChangeAssignee={handleChangeAssignee}
									onRequestLinkDeliverable={(clicked) => {
										setLinkTask(clicked);
										setLinkDeliverableId(nullSafeGet('deliverableId', clicked));
									}}
									onDelete={handleDelete}
								/>
							),
						},
					]}
				/>
			</div>

			<Modal
				visible={linkTask != null}
				title="Link deliverable"
				okText="Link"
				onOk={applyLinkDeliverable}
				onCancel={() => setLinkTask(null)}
				destroyOnClose
			>
				<Select
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="Select a deliverable"
					value={linkDeliverableId}
					onChange={(value) => setLinkDeliverableId(value)}
					style={{ width: '100%' }}
					options={(deliverables || []).map((entry) => ({
						value: nullSafeGet('id', entry),
						label: nullSafeGet('name', entry),
					}))}
				/>
			</Modal>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	// Both TCs are registered in their slice's initial state, so the key is always
	// present and getRecordsForTargetCollection won't throw.
	contacts: getRecordsForTargetCollection(state.buyer_contacts, CONTACTS_TC),
	filterContacts: getRecordsForTargetCollection(state.buyer_contacts, FILTER_CONTACTS_TC),
	deliverables: getRecordsForTargetCollection(state.deliverables, DELIVERABLES_TC),
	tasksSlice: state.project_tasks,
});

const mapDispatchToProps = (dispatch, ownProps) => {
	const isSupplier = ownProps.userType === ROLE_TYPES.SUPPLIER;
	const taskThunks = isSupplier
		? projectTasksRestCrudThunksForSupplier
		: projectTasksRestCrudThunksForBuyer;
	const deliverableThunks = isSupplier
		? deliverablesRestCrudThunksForSupplier
		: deliverablesRestCrudThunksForBuyer;
	const contactThunks = isSupplier
		? buyerContactsRestCrudThunksForSupplier
		: buyerContactsRestCrudThunksForBuyer;
	const projectThunks = isSupplier
		? projectsRestCrudThunksForSupplier
		: projectsRestCrudThunksForBuyer;
	const tc = tcFor(ownProps.deliverableId);
	return {
		// PaginatedReduxTable dispatches this thunk itself, so return it (don't dispatch).
		// It receives (additionalParams, tc, pagination, sorting, sliceFilters): strip the
		// refresh nonce and translate the slice filters to the server params the backend
		// supports (isCompleted + deliverableId + assignedTo); DEFAULT_SORTING is due-date
		// ascending.
		fetchTasksForList: (params, targetCollectionName, pagination, sorting, filters) => {
			const nextParams: any = { ...(params || {}) };
			delete nextParams._refresh;
			const status = nullSafeGet('status', filters);
			if (status && status !== 'all') {
				nextParams.isCompleted = status === 'complete';
			}
			if (ownProps.deliverableId == null && nullSafeGet('deliverableId', filters)) {
				nextParams.deliverableId = Number(nullSafeGet('deliverableId', filters));
			}
			if (nullSafeGet('assignedTo', filters)) {
				nextParams.assignedTo = nullSafeGet('assignedTo', filters);
			}
			return taskThunks.read(
				nextParams,
				targetCollectionName,
				pagination,
				sorting || DEFAULT_SORTING
			);
		},
		createTask: (entity) => dispatch(taskThunks.create(entity, 'id', tc)),
		updateTask: (entity) => dispatch(taskThunks.update(entity)),
		// Immediate: the delete is already confirmed via Popconfirm, so skip the
		// default 4s undo delay (which otherwise leaves the row on screen).
		deleteTask: (entity) => dispatch(taskThunks.deleteImmediate(entity)),
		refreshDeliverable: (id) => dispatch(deliverableThunks.readOne(id)),
		// Keeps the project's taskProjectMetrics (the header fraction on the project
		// Tasks tab) in step with a write.
		refreshProject: (id) => dispatch(projectThunks.readOne(id)),
		fetchContacts: (search) =>
			dispatch(contactThunks.read({ search, limit: CONTACTS_PAGE_SIZE, offset: 0 }, CONTACTS_TC)),
		fetchFilterContacts: (search) =>
			dispatch(
				contactThunks.read({ search, limit: CONTACTS_PAGE_SIZE, offset: 0 }, FILTER_CONTACTS_TC)
			),
		fetchDeliverables: (projectId) =>
			dispatch(
				deliverableThunks.read({ projectId: Number(projectId) }, DELIVERABLES_TC, {
					current: 1,
					pageSize: 100,
				})
			),
		updateFilters: (updatedFilters, targetCollectionName) =>
			dispatch(
				PROJECT_TASKS_CRUD_ACTION_CREATORS.updateFilters(updatedFilters, targetCollectionName)
			),
		updateSorting: (sortBy, order, targetCollectionName) =>
			dispatch(
				PROJECT_TASKS_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollectionName)
			),
		clearAndUpdateFilters: (updatedFilters, targetCollectionName) =>
			dispatch(
				PROJECT_TASKS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
					updatedFilters,
					targetCollectionName
				)
			),
	};
};

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(DeliverableTasks));
